import * as DataDog from './DataDog';
import * as Sentry from './Sentry';

init();

interface User {
  id: string;
  carrier_guid: string;
  carrier_name?: string;
  company_type?: string;
  driverGuid?: string;
  deviceGuid?: string;
  [key: string]: unknown;
}

interface LogExceptionOptions {
  extraInfo?: Record<string, unknown>;
}

function init() {
  if ('Cypress' in window) return;
  if (import.meta.env.NODE_ENV === 'test') return;
  if (import.meta.env.VITE_APP_TARGET === 'local') return;

  try {
    Sentry.init();
  } catch {
    // ignore
  }

  try {
    DataDog.init();
  } catch {
    // ignore
  }
}

function requestForceReload() {
  if (
    // eslint-disable-next-line no-alert
    window.confirm(
      'Failed to load required assets. This can happen either due to bad internet connection or a new version of the website. Reload the page?\n' +
        '(Clear your browser cache and reload the page manually if this error occurs again)',
    )
  ) {
    window.location.reload();
  }
}

export function identifyUser(user: User) {
  DataDog.identifyUser(user);
  Sentry.identifyUser(user);
}

export function clearUser() {
  DataDog.clearUser();
  Sentry.cleanUser();
}

const moduleImportErrorMessages = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
];

const moduleImportErrorMessagesRegExp = new RegExp(
  moduleImportErrorMessages.join('|'),
  'i',
);

function isModuleImportError(error: unknown) {
  return (
    error instanceof Error &&
    moduleImportErrorMessagesRegExp.test(error.message)
  );
}

export function logError(
  error: unknown,
  namespace: string,
  { extraInfo }: LogExceptionOptions = {},
) {
  const messageContext = { namespace, extraInfo, original_error: error };

  if (typeof error === 'string') {
    DataDog.getLogger().error(error, messageContext);
    Sentry.getLogger().error(error, messageContext);
  } else if (error instanceof Error) {
    DataDog.getLogger().error(error.message, messageContext, error);
    Sentry.getLogger().error(error.message, messageContext);
  } else {
    Sentry.getLogger().error('Unknown Error', messageContext);
    DataDog.getLogger().error(
      'Unknown Error',
      messageContext,
      new Error('Unknown Error'), // create inline Error to include stacktrace
    );
  }

  if (isModuleImportError(error)) {
    setTimeout(() => {
      requestForceReload();
    }, 2000);
  }
}

export function logInfo(message: string, extraInfo?: Record<string, unknown>) {
  DataDog.getLogger().info(message, extraInfo);
  Sentry.getLogger().info(message, extraInfo);
}

export function logWarning(
  message: string,
  extraInfo?: Record<string, unknown>,
) {
  DataDog.getLogger().warn(message, extraInfo);
  Sentry.getLogger().warn(message, extraInfo);
}

export function addLogContext(key: string, value: Record<string, unknown>) {
  DataDog.getLogger().setContextProperty(`context.${key}`, value);
  Sentry.addContext(`context.${key}`, value);
}
